// src/modules/panelGlobal/components/ClientePlataformaVehiculos.jsx
// Vista de la plataforma de rastreo de un cliente (Wialon/Leasecontrol/TRS
// Rastreo) para una plataforma FIJA (una pestana por plataforma en Historial,
// a diferencia de Panel Global que resuelve una sola por prioridad) — permite
// vincular un usuario existente o registrar uno nuevo (incluso para un cliente
// que todavia no tiene ningun vinculo real en esa plataforma), pero sin
// desvincular/cambiar plataforma ni revision — pensada para un usuario comun.
import React, { useEffect, useState } from "react";
import { Box, Stack, Typography, Chip, CircularProgress, Alert } from "@mui/material";
import { getPanelGlobalDetalle, getPanelGlobalVehiculos } from "../services/panelGlobalService";
import { WialonLeasecontrolDetalle, TrsRastreoDetalle, EnlazarUsuarioForm } from "./ClientePlataformaPanel";

const PLATAFORMA_LABEL = {
  wialon: { label: "Wialon", color: "info" },
  leasecontrol: { label: "Leasecontrol", color: "secondary" },
  trsrastreo: { label: "TRS Rastreo", color: "success" },
};

export default function ClientePlataformaVehiculos({ codServicioCliente, plataforma }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detalle, setDetalle] = useState(null);
  const [sortPlacaPorNumSim, setSortPlacaPorNumSim] = useState(new Map());
  const [sortPlacas, setSortPlacas] = useState(new Set());

  const fetchDetalle = () => {
    if (!codServicioCliente) return Promise.resolve();
    return Promise.all([
      getPanelGlobalDetalle(codServicioCliente, plataforma),
      getPanelGlobalVehiculos(codServicioCliente, { page: 1, limit: 200 }),
    ]).then(([{ data: det }, { data: sortData }]) => {
      setDetalle(det);

      const items = sortData.data || [];
      const mapa = new Map();
      items.forEach((v) => {
        if (v.num_sim) mapa.set(String(v.num_sim), v.placa);
        if (v.emai) mapa.set(String(v.emai), v.placa);
      });
      setSortPlacaPorNumSim(mapa);
      setSortPlacas(new Set(items.map((v) => String(v.placa || "").trim().toUpperCase()).filter(Boolean)));
    });
  };

  useEffect(() => {
    if (!codServicioCliente) return;
    let cancelado = false;
    setLoading(true);
    setError("");
    setDetalle(null);

    fetchDetalle()
      .catch(() => { if (!cancelado) setError("No se pudo cargar la información de la plataforma."); })
      .finally(() => { if (!cancelado) setLoading(false); });

    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codServicioCliente, plataforma]);

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} /></Box>;
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!detalle || !detalle.plataforma) {
    const label = plataforma ? PLATAFORMA_LABEL[plataforma]?.label || plataforma : "ninguna plataforma de rastreo";
    return <Alert severity="info">Este cliente no está vinculado a {label}.</Alert>;
  }

  const cfg = PLATAFORMA_LABEL[detalle.plataforma] || { label: detalle.plataforma, color: "default" };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Plataforma de rastreo</Typography>
        <Chip size="small" color={cfg.color} label={cfg.label} />
      </Stack>

      {/* sin_plataforma: sin vinculo real todavia en esta plataforma puntual —
          se muestra igual (en vez de solo un Alert) para poder vincular un
          usuario existente o registrar uno nuevo desde cero. */}
      {detalle.sin_plataforma && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Este cliente todavía no está vinculado a {cfg.label}. Vincula un usuario existente o registra uno nuevo.
        </Alert>
      )}

      {detalle.plataforma === "trsrastreo" ? (
        <TrsRastreoDetalle
          detalle={detalle}
          sortPlacas={sortPlacas}
          codServicioCliente={codServicioCliente}
          onRefresh={fetchDetalle}
        />
      ) : (
        <>
          <EnlazarUsuarioForm
            plataforma={detalle.plataforma}
            codServicioCliente={codServicioCliente}
            onVinculado={fetchDetalle}
          />
          <WialonLeasecontrolDetalle
            plataforma={detalle.plataforma}
            externalUserIds={detalle.external_user_ids || []}
            codServicioCliente={codServicioCliente}
            sortPlacaPorNumSim={sortPlacaPorNumSim}
            readOnly
          />
        </>
      )}
    </Box>
  );
}
